import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface ComposerActionRowProps {
  /** Actions to display on the left (chips, buttons) */
  leftSlot?: ReactNode;
  /** Actions to display on the right (send button) */
  rightSlot?: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * ComposerActionRow — horizontal row for composer actions.
 *
 * Provides left and right slots for flexible action placement:
 * - Left: typically action chips (Follow-ups, Orders, Notepad)
 * - Right: typically the send button
 *
 * @example
 * ```tsx
 * <ComposerActionRow
 *   leftSlot={
 *     <>
 *       <ActionChip icon={ListTodo} label="Follow-ups" />
 *       <ActionChip icon={FileText} label="Orders" />
 *     </>
 *   }
 *   rightSlot={<SendButton onClick={handleSend} />}
 * />
 * ```
 */
export function ComposerActionRow({ leftSlot, rightSlot, className }: ComposerActionRowProps) {
  return (
    <div className={cn('flex items-center justify-between gap-2', className)}>
      {/* Left slot: action chips */}
      <div className="flex items-center gap-1.5">{leftSlot}</div>

      {/* Right slot: send button */}
      <div className="flex items-center gap-1.5">{rightSlot}</div>
    </div>
  );
}
